import React, { useEffect, useRef, useState } from 'react';
import { Button, Paper } from '@material-ui/core';

import { customerApi as api } from 'api/customer';
import { Notifications } from 'shared';

import { CustomerAccount } from './CustomerAccount';
import { CustomerSearchBar } from './CustomerSearchBar';
import { SearchResults } from './SearchResults';
import { ThirdPartyOrder } from './ThirdPartyOrder';

function SearchPagination({ hasNextPage, onNext, onPrevious, pageNumber }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
            {pageNumber > 0 ? (
                <Button type="button" variant="outlined" onClick={onPrevious} style={{ margin: '10px' }}>
                    Previous
                </Button>
            ) : null}
            <span>Page {pageNumber + 1}</span>
            {hasNextPage ? (
                <Button type="button" variant="outlined" onClick={onNext} style={{ margin: '10px' }}>
                    Next
                </Button>
            ) : null}
        </div>
    );
}

function CustManage() {
    const [notification, setNotification] = useState({ text: '', level: '' });
    const [searchResults, setSearchResults] = useState([]);

    // Pagination states
    const [searchPageNumber, setSearchPageNumber] = useState(0);
    const [hasPagination, setHasPagination] = useState(false);
    const [nextPageNumber, setNextPageNumber] = useState(null);
    const [pageCache, setPageCache] = useState({});
    const searchRequestId = useRef(0);
    const hasNextPage = nextPageNumber !== null && nextPageNumber > searchPageNumber;

    const [searchingCustomers, setSearchingCustomers] = useState(false);
    const [customerID, setCustomerID] = useState('');
    const [v2Plans, setV2Plans] = useState([]);
    const [isThirdParty, setIsThirdParty] = useState(false);
    const [thirdPartySource, setThirdPartySource] = useState('');

    const urlParams = new URLSearchParams(window.location.search);
    const searchType = urlParams.get('searchType');
    const searchValue = urlParams.get('value');
    const exactValue = urlParams.get('exact');

    const getV2Plans = async () => {
        const res = await api.getV2Plans();
        if (!res.status) {
            setNotification({ text: res.msg, level: 'error' });
            return;
        }

        setV2Plans(res.data.plans);
    };

    const showCustomerPage = (customers, pageInfo, requestedPage) => {
        setSearchingCustomers(false);
        setIsThirdParty(false);
        setThirdPartySource('');

        const currentPage = pageInfo?.currentPage ?? requestedPage;
        const followingPage = typeof pageInfo?.nextPage === 'number' ? pageInfo.nextPage : null;

        // A lone match on the first page opens that customer. A lone match on a later page stays in the list.
        if (customers.length === 1 && currentPage === 0) {
            setCustomerID(customers[0].customerID);
            setSearchResults([]);
            setHasPagination(false);
            setSearchPageNumber(0);
            setNextPageNumber(null);
            return;
        }

        setCustomerID(0);
        setSearchResults(customers);
        setSearchPageNumber(currentPage);
        setNextPageNumber(followingPage);
        setHasPagination(Boolean(pageInfo));
    };

    const searchCustomers = async ({ exact = false, searchBy, searchPageNumber: requestedPage = 0, searchTerm }) => {
        let pageNumber = requestedPage ?? 0;
        const requestId = searchRequestId.current + 1;
        searchRequestId.current = requestId;

        const urlExact = exactValue === 'true';
        const searchChanged = searchBy !== searchType || searchTerm !== searchValue || exact !== urlExact;

        const cache = searchChanged ? {} : pageCache;
        if (searchChanged) {
            setPageCache({});
            setSearchPageNumber(0);
            pageNumber = 0;
        }

        if (searchBy !== searchType) {
            urlParams.set('searchType', searchBy);
        }

        if (searchTerm !== searchValue) {
            urlParams.set('value', searchTerm);
        }

        if (exact !== urlExact) {
            if (exact) {
                urlParams.set('exact', 'true');
            } else {
                urlParams.delete('exact');
            }
        }

        const newURL = `${window.location.pathname}?${urlParams}`;
        window.history.replaceState(null, null, newURL);

        const cachedPage = cache[pageNumber];
        if (cachedPage) {
            showCustomerPage(cachedPage.customers, cachedPage.page, pageNumber);
            return;
        }

        setSearchingCustomers(true);
        setCustomerID(0);
        setSearchResults([]);
        setIsThirdParty(false);
        setThirdPartySource('');
        setHasPagination(false);
        setNextPageNumber(null);

        const results = await api.searchCustomers(searchTerm, searchBy, pageNumber, exact);
        if (searchRequestId.current !== requestId) {
            return;
        }

        setSearchingCustomers(false);
        if (!results.status || !results.data) {
            setNotification({
                text: `Error searching customers${results.msg ? `: ${results.msg}` : ''}`,
                level: 'error',
            });
            return;
        }

        if (results.data.isThirdParty) {
            setCustomerID(0);
            setIsThirdParty(true);
            setThirdPartySource(results.data.thirdPartySource);
            return;
        }

        if (!results.data.customers) {
            setNotification({ text: 'Error searching customers', level: 'error' });
            return;
        }

        const pageInfo = results.data.page ?? null;
        const currentPage = pageInfo?.currentPage ?? pageNumber;
        setPageCache((current) => {
            const base = searchChanged ? {} : current;
            return {
                ...base,
                [currentPage]: { customers: results.data.customers, page: pageInfo },
            };
        });
        showCustomerPage(results.data.customers, pageInfo, pageNumber);
    };

    useEffect(() => {
        getV2Plans();

        if (searchType && searchValue) {
            searchCustomers({
                searchBy: searchType,
                searchTerm: searchValue,
                searchPageNumber: 0,
                exact: exactValue === 'true',
            });
        }
    }, []);

    return (
        <Paper style={{ minHeight: '80vh', padding: '10px', backgroundColor: '#FFF9EA' }}>
            <Notifications options={notification} />
            <h1>Customer Management Portal</h1>
            <CustomerSearchBar
                exact={exactValue === 'true'}
                searchCustomers={searchCustomers}
                searchType={searchType}
                searchValue={searchValue}
            />

            {customerID > 0 && (
                <CustomerAccount setNotification={setNotification} customerID={customerID} v2Plans={v2Plans} />
            )}
            {customerID === 0 && searchingCustomers ? <div>Searching</div> : null}
            {customerID === 0 && !searchingCustomers && isThirdParty ? (
                <ThirdPartyOrder source={thirdPartySource} searchTerm={searchValue} />
            ) : null}
            {customerID === 0 && !searchingCustomers && !isThirdParty ? (
                <>
                    <SearchResults searchResults={searchResults} setCustomerID={setCustomerID} />
                    {hasPagination && searchResults.length > 0 ? (
                        <SearchPagination
                            hasNextPage={hasNextPage}
                            pageNumber={searchPageNumber}
                            onNext={() =>
                                searchCustomers({
                                    exact: exactValue === 'true',
                                    searchBy: searchType,
                                    searchPageNumber: nextPageNumber ?? searchPageNumber + 1,
                                    searchTerm: searchValue,
                                })
                            }
                            onPrevious={() =>
                                searchCustomers({
                                    exact: exactValue === 'true',
                                    searchBy: searchType,
                                    searchPageNumber: searchPageNumber - 1,
                                    searchTerm: searchValue,
                                })
                            }
                        />
                    ) : null}
                </>
            ) : null}
        </Paper>
    );
}

export { CustManage };
